import type { AppointmentType } from '../../../contexts/PsychologistContext';

export interface BookingPatient {
  id?: string;
  name?: string;
  email: string;
  phone?: string;
  process?: string;
  processType?: string;
  timezone?: string;
  nationality?: string;
  hasNextSessionDate?: boolean;
}

export interface BookingWorkSchedule {
  id: string | number;
  psychologistId: string | number;
  weekday: number;
  startTime: string;
  endTime: string;
  active: boolean;
}

/**
 * Strips accents/diacritical marks, lowercases, and trims.
 */
export function normalizeText(s?: string | null): string {
  return (s || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Filters appointment types compatible with the patient's processType.
 * Falls back to all types if processType is empty or matches nothing.
 */
export function getCompatibleAppointmentTypes(
  baseTypes: AppointmentType[],
  processType?: string | null,
): AppointmentType[] {
  const pt = normalizeText(processType);
  if (!pt) return baseTypes;
  const filtered = baseTypes.filter(type => {
    const byService = normalizeText(type.service as unknown as string) === pt;
    const byName = normalizeText(type.name) === pt || normalizeText(type.name).includes(pt);
    return byService || byName;
  });
  return filtered.length > 0 ? filtered : baseTypes;
}

/**
 * Resolves the patient for scheduling: prefers selectedPatient,
 * then looks up by email. Returns null if not found (never returns mock).
 */
export function resolvePatientForScheduling(
  selectedPatient: BookingPatient | undefined | null,
  effectivePatients: BookingPatient[],
  email: string,
): BookingPatient | null {
  const found = selectedPatient ?? effectivePatients.find(px => px.email === email);
  return found ?? null;
}

/**
 * Resolves the effective appointment type via fallback chain:
 * 1. selectedType (if already set)
 * 2. Match by normalized name vs processType
 * 3. First available type
 * 4. Mock type (only for mock patients)
 * 5. null (for real patients when nothing available)
 */
export function resolveEffectiveAppointmentType(
  selectedType: AppointmentType | null | undefined,
  baseTypes: AppointmentType[],
  processType: string | undefined | null,
  isMockPatient: boolean,
): AppointmentType | null {
  return (
    selectedType
    ?? baseTypes.find(t => normalizeText(t.name) === normalizeText(processType || ''))
    ?? baseTypes[0]
    ?? (isMockPatient
      ? { id: 'mock-type', name: processType || 'Individual', duration: 60 } as AppointmentType
      : null)
  );
}

/**
 * Validates that the selected appointment type matches the patient's processType.
 * Skips validation when processType is empty/null.
 */
export function validateAppointmentTypeMatch(
  appointmentType: AppointmentType,
  processType?: string | null,
): boolean {
  if (!processType) return true;
  const selService = normalizeText(String((appointmentType as unknown as { service?: string })?.service));
  const selName = normalizeText(appointmentType.name);
  const patientPT = normalizeText(String(processType));
  return !!(
    (selService && selService === patientPT)
    || (selName && selName === patientPT)
    || (selName && selName.includes(patientPT))
  );
}

/**
 * Merges API-returned template with optimistic items from previous state.
 * Preserves optimistic items whose IDs are not yet in the API response.
 */
export function mergeTemplateWithOptimistic(
  apiTemplate: BookingWorkSchedule[],
  prevTemplate: BookingWorkSchedule[],
): BookingWorkSchedule[] {
  const tplIds = new Set(apiTemplate.map(t => String(t.id)));
  const optimistic = prevTemplate.filter(t => !tplIds.has(String(t.id)));
  return optimistic.length > 0 ? [...apiTemplate, ...optimistic] : apiTemplate;
}
